import React from 'react';
import { useNotifications, Notification } from '../../hooks/useNotifications';
import {
  Drawer,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Typography,
  Box,
  Badge,
  Chip,
  Button,
  makeStyles,
} from '@material-ui/core';
import NotificationsIcon from '@material-ui/icons/Notifications';
import CloseIcon from '@material-ui/icons/Close';
import DeleteIcon from '@material-ui/icons/Delete';
import CheckIcon from '@material-ui/icons/Check';
import InfoIcon from '@material-ui/icons/Info';
import WarningIcon from '@material-ui/icons/Warning';
import ErrorIcon from '@material-ui/icons/Error';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { Link } from 'react-router-dom';
import { Progress } from '@backstage/core-components';

const useStyles = makeStyles(theme => ({
  drawer: {
    width: 400,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  notificationItem: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  unreadItem: {
    backgroundColor: theme.palette.action.selected,
  },
  notificationContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  notificationActions: {
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  emptyState: {
    padding: theme.spacing(4),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
  timestamp: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
  iconButton: {
    padding: theme.spacing(1),
  },
}));

export const NotificationsButton: React.FC = () => {
  const classes = useStyles();
  const [open, setOpen] = React.useState(false);
  const notificationsHook = useNotifications();

  return (
    <>
      <IconButton
        color="inherit"
        onClick={() => setOpen(true)}
        className={classes.iconButton}
      >
        <Badge badgeContent={notificationsHook.unreadCount} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>
      <NotificationsPanel 
        open={open} 
        onClose={() => setOpen(false)} 
        notificationsHook={notificationsHook}
      />
    </>
  );
};

interface NotificationsPanelProps {
  open: boolean;
  onClose: () => void;
  notificationsHook: ReturnType<typeof useNotifications>;
}

export const NotificationsPanel: React.FC<NotificationsPanelProps> = ({ open, onClose, notificationsHook }) => {
  const classes = useStyles();
  const {
    notifications,
    loading,
    unreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    deleteAllNotifications,
  } = notificationsHook;

  const getIcon = (type: Notification['type']) => {
    switch (type) {
      case 'info':
        return <InfoIcon style={{ color: '#2196f3' }} />;
      case 'warning':
        return <WarningIcon style={{ color: '#ff9800' }} />;
      case 'error':
        return <ErrorIcon style={{ color: '#f44336' }} />;
      case 'success':
        return <CheckCircleIcon style={{ color: '#4caf50' }} />;
      default:
        return <InfoIcon />;
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box className={classes.drawer}>
        <Box className={classes.header}>
          <Typography variant="h6">
            Notifications
            {unreadCount > 0 && (
              <Chip
                label={unreadCount}
                size="small"
                color="primary"
                style={{ marginLeft: 8 }}
              />
            )}
          </Typography>
          <Box>
            {unreadCount > 0 && (
              <Button
                size="small"
                startIcon={<CheckIcon />}
                onClick={markAllAsRead}
                style={{ marginRight: 8 }}
              >
                Mark all read
              </Button>
            )}
            {notifications.length > 0 && (
              <Button
                size="small"
                startIcon={<DeleteIcon />}
                onClick={deleteAllNotifications}
                style={{ marginRight: 8 }}
                color="secondary"
              >
                Clear all
              </Button>
            )}
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {loading ? (
          <Box p={2}>
            <Progress />
          </Box>
        ) : notifications.length === 0 ? (
          <Box className={classes.emptyState}>
            <NotificationsIcon style={{ fontSize: 64, opacity: 0.3 }} />
            <Typography variant="body1">No notifications</Typography>
          </Box>
        ) : (
          <List>
            {notifications.map(notification => (
              <ListItem
                key={notification.id}
                className={`${classes.notificationItem} ${!notification.read ? classes.unreadItem : ''}`}
                alignItems="flex-start"
              >
                <Box mr={2} mt={1}>
                  {getIcon(notification.type)}
                </Box>
                <Box className={classes.notificationContent} flex={1}>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center">
                        <Typography variant="subtitle2">
                          {notification.title}
                        </Typography>
                        {notification.isGroup && notification.groupName && (
                          <Chip
                            label={notification.groupName}
                            size="small"
                            color="default"
                            variant="outlined"
                            style={{ marginLeft: 8, height: 20, fontSize: '0.7rem' }}
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <>
                        <Typography variant="body2" color="textSecondary">
                          {notification.message}
                        </Typography>
                        <Typography className={classes.timestamp}>
                          {formatTimestamp(notification.timestamp)}
                        </Typography>
                      </>
                    }
                  />
                  <Box className={classes.notificationActions}>
                    {notification.link && (
                      <Button
                        size="small"
                        color="primary"
                        component={Link}
                        to={notification.link}
                        onClick={onClose}
                      >
                        View
                      </Button>
                    )}
                    {!notification.read && (
                      <Button
                        size="small"
                        onClick={() => markAsRead(notification.id)}
                      >
                        Mark read
                      </Button>
                    )}
                    <IconButton
                      size="small"
                      onClick={() => deleteNotification(notification.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </ListItem>
            ))}
          </List>
        )}
      </Box>
    </Drawer>
  );
};

